import React, { useEffect } from "react";
import {
    Modal,
    ModalOverlay,
    ModalContent,
    ModalBody,
    ModalCloseButton,
    Box,
    Flex,
    HStack,
    Icon,
    Image,
    Text,
    VStack,
} from "@chakra-ui/react";
import { IoShieldCheckmarkOutline, IoSparklesOutline } from "react-icons/io5";
import { AccountArea } from "../Structure/accountArea";
import { useAuthState } from "react-firebase-hooks/auth";
import { doc, getDoc } from "firebase/firestore";
import { auth, db } from "../../firebase/firebase-config";
import { signOutIfProfileIncomplete } from "../../lib/socialAuth";
import icon from "../../Assets/Images/icon.png";

export const SignInModal = ({ isOpen, onClose }) => {
    const [user] = useAuthState(auth);

    // Close once a finished profile exists. An in-flight check from before
    // the profile was written must not run again and fight the name step.
    useEffect(() => {
        if (!user || !isOpen) return undefined;
        let cancelled = false;
        const checkUserProfile = async () => {
            try {
                const userDoc = await getDoc(doc(db, "users", user.uid));
                if (!cancelled && userDoc.exists() && userDoc.data().displayName) {
                    onClose();
                }
            } catch (error) {
                console.error("Error checking user profile:", error);
            }
        };
        checkUserProfile();
        return () => {
            cancelled = true;
        };
    }, [user, isOpen, onClose]);

    const handleDismiss = async () => {
        await signOutIfProfileIncomplete();
        onClose();
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={handleDismiss}
            isCentered
            size={{ base: "full", md: "md" }}
            scrollBehavior="inside"
        >
            {/* Bump the modal above any lingering Menu dropdowns from the navbar
                (Chakra's default Menu z-index can land on top of a modal in
                some edge cases — forcing these well above "modal" keeps the
                sign-in flow as the top-most layer). */}
            <ModalOverlay
                backdropFilter="blur(4px)"
                bg="blackAlpha.500"
                zIndex={2000}
            />
            <ModalContent
                borderRadius={{ base: 0, md: "2xl" }}
                overflow="hidden"
                containerProps={{ zIndex: 2000 }}
            >
                <ModalCloseButton
                    top={4}
                    right={4}
                    color="white"
                    _hover={{ bg: "whiteAlpha.300" }}
                    zIndex={2}
                />

                {/* Branded header — matches the blue header strip used in the
                    other modals, so the look is consistent across the app.
                    Soft decorative orbs give the block depth and keep it
                    from reading as a flat color bar. */}
                <Box
                    bgGradient="linear(to-br, #508cf7, #226cf1)"
                    color="white"
                    px={6}
                    pt={7}
                    pb={8}
                    position="relative"
                    overflow="hidden"
                >
                    <Box
                        position="absolute"
                        top="-60px"
                        right="-40px"
                        w="180px"
                        h="180px"
                        borderRadius="full"
                        bg="whiteAlpha.200"
                        filter="blur(30px)"
                        pointerEvents="none"
                    />
                    <Box
                        position="absolute"
                        bottom="-70px"
                        left="-40px"
                        w="160px"
                        h="160px"
                        borderRadius="full"
                        bg="whiteAlpha.100"
                        filter="blur(30px)"
                        pointerEvents="none"
                    />

                    <HStack spacing={3} align="center" mb={5} position="relative">
                        <Box
                            bg="white"
                            p={2}
                            borderRadius="xl"
                            boxShadow="0 6px 18px rgba(15,23,42,0.18)"
                        >
                            <Image
                                src={icon}
                                alt="Proximy"
                                boxSize="28px"
                                objectFit="contain"
                                borderRadius="md"
                            />
                        </Box>
                        <HStack
                            spacing={1.5}
                            bg="whiteAlpha.300"
                            px={2.5}
                            py={1}
                            borderRadius="full"
                            backdropFilter="blur(4px)"
                        >
                            <Icon as={IoSparklesOutline} boxSize="11px" />
                            <Text
                                fontSize="10px"
                                fontWeight="700"
                                letterSpacing="0.08em"
                                textTransform="uppercase"
                            >
                                Sign in
                            </Text>
                        </HStack>
                    </HStack>

                    <Text
                        className="font-gilroy-bold"
                        fontSize="26px"
                        fontWeight="700"
                        lineHeight="1.15"
                        position="relative"
                    >
                        Welcome to Proximy
                    </Text>
                    <Text
                        fontSize="13px"
                        opacity={0.9}
                        mt={1.5}
                        position="relative"
                    >
                        Sign in or create an account to get started.
                    </Text>
                </Box>

                <ModalBody p={0}>
                    <VStack spacing={0} align="stretch">
                        {/* Extra top padding so the phone input has room to
                            breathe after the blue header. */}
                        <Box pt={6}>
                            <AccountArea isOpen={isOpen} onSignupComplete={onClose} />
                        </Box>

                        {/* Reassurance footer, same visual language as the
                            secondary callouts in the other modals. */}
                        <Flex
                            px={5}
                            py={3}
                            borderTop="1px solid"
                            borderColor="gray.100"
                            bg="gray.50"
                            align="center"
                            justify="center"
                            gap={4}
                            flexWrap="wrap"
                        >
                            <HStack spacing={1.5}>
                                <Icon
                                    as={IoShieldCheckmarkOutline}
                                    color="#508cf7"
                                    boxSize="14px"
                                />
                                <Text fontSize="11px" color="gray.500" fontWeight="500">
                                    Secure sign-in
                                </Text>
                            </HStack>
                            <HStack spacing={1.5}>
                                <Icon
                                    as={IoSparklesOutline}
                                    color="#508cf7"
                                    boxSize="14px"
                                />
                                <Text fontSize="11px" color="gray.500" fontWeight="500">
                                    No password needed
                                </Text>
                            </HStack>
                        </Flex>

                        {/* Legal — small print, still always reachable. */}
                        <Text
                            px={5}
                            py={3}
                            fontSize="10px"
                            color="gray.400"
                            textAlign="center"
                            lineHeight="1.5"
                        >
                            By continuing, you agree to our{" "}
                            <Box
                                as="a"
                                href="https://proximy-site.web.app/terms"
                                target="_blank"
                                rel="noopener noreferrer"
                                color="gray.500"
                                textDecoration="underline"
                                _hover={{ color: "#508cf7" }}
                            >
                                Terms
                            </Box>{" "}
                            and{" "}
                            <Box
                                as="a"
                                href="https://proximy-site.web.app/privacy"
                                target="_blank"
                                rel="noopener noreferrer"
                                color="gray.500"
                                textDecoration="underline"
                                _hover={{ color: "#508cf7" }}
                            >
                                Privacy Policy
                            </Box>
                            .
                        </Text>
                    </VStack>
                </ModalBody>
            </ModalContent>
        </Modal>
    );
};
